/**
 * The frame every console screen sits in, measured from Salesforge's own.
 *
 * Salesforge draws one band at the top of the content column: 16px of white,
 * a 40px row with the page's icon, title and help mark on the left and its
 * buttons on the right, then 20px, then the tab strip with a rule under it
 * that runs the full width of the column. The content below it is inset 32px
 * left and right, 20px from the rule, with 80px of room at the bottom.
 *
 * A screen that has no tabs gets the rule under the title row instead, so the
 * band always ends on a line. A screen that runs edge to edge under the band
 * (the inbox, a canvas) passes `bleed` and lays out its own content.
 *
 * Routes that use this frame are marked `fullBleed` in the route table, so the
 * shell adds no padding of its own around it.
 */
import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { cn } from '../lib/cn'
import { useDocumentTitle } from '../lib/hooks'
import { InfoHint } from './InfoHint'
import { ScrollableStrip } from './ScrollableStrip'

export interface PageTab {
  to: string
  label: ReactNode
  /** A count chip after the label, as Salesforge puts on a few of its tabs. */
  count?: number
  /** Match the path exactly, for the tab that is the section's index. */
  end?: boolean
  /** Carried through to the link, e.g. the list a takeover was opened from. */
  state?: unknown
}

export interface PageProps {
  title: ReactNode
  /** A 16px glyph before the title, in the subtle ink. */
  icon?: ReactNode
  /** What the page is for, in a tooltip behind the help mark beside the title. */
  help?: ReactNode
  /** The buttons at the right of the title row, 40px tall, at most one primary. */
  actions?: ReactNode
  /** Route tabs under the title row. */
  tabs?: readonly PageTab[]
  /** Anything that replaces the tab strip, e.g. a strip with its own state. */
  tabStrip?: ReactNode
  /** Content runs edge to edge below the band, with no inset and no scroll of its own. */
  bleed?: boolean
  /**
   * Below 768px the title row is kept for screen readers only and the band
   * starts with the tab strip, for a screen whose phone menu already names it
   * (One at a time in the Queue).
   */
  hideTitleBelowMd?: boolean
  children: ReactNode
  className?: string
}

export function Page({ title, icon, help, actions, tabs, tabStrip, bleed = false, hideTitleBelowMd = false, children, className }: PageProps) {
  // The browser tab says which screen this is. A screen that names a narrower
  // title itself (a tab of Settings, say) still wins: its own hook runs after
  // this one, because a parent's effects run after its children's.
  useDocumentTitle(typeof title === 'string' ? title : undefined)
  const strip = tabStrip ?? (tabs && tabs.length > 0 ? <PageTabs tabs={tabs} /> : null)
  return (
    <div className={cn('flex min-h-full flex-col', bleed && 'h-full min-h-0', className)}>
      <div className={cn('shrink-0 bg-surface pt-4', strip === null && 'border-b border-line pb-4', hideTitleBelowMd && 'max-md:pt-2.5')}>
        <div className={cn('flex h-10 items-center justify-between gap-4 px-4 lg:px-8', hideTitleBelowMd && 'max-md:sr-only')}>
          <div className="flex min-w-0 items-center gap-2 px-2 py-1">
            {icon ? <span className="grid h-4 w-4 shrink-0 place-items-center text-ink-subtle [&>svg]:h-4 [&>svg]:w-4">{icon}</span> : null}
            <h1 className="truncate text-base font-semibold text-ink-secondary">{title}</h1>
            {help ? (
              <InfoHint label={typeof title === 'string' ? title : 'this page'} className="-ml-1">
                {help}
              </InfoHint>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null}
        </div>
        {strip ? <div className={cn('mt-5', hideTitleBelowMd && 'max-md:mt-0')}>{strip}</div> : null}
      </div>
      {bleed ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : (
        <div className="flex-1 px-4 pb-20 pt-5 lg:px-8">{children}</div>
      )}
    </div>
  )
}

/**
 * Salesforge's page tabs: 14px semibold, 4px either side, 11px under the
 * words, a 2px underline on the one that is open, 16px apart, on a rule that
 * runs the full width of the column.
 *
 * When the tabs do not fit, the strip scrolls sideways with no bar, and
 * `ScrollableStrip` gives a mouse the arrows, the edge hover and the wheel to
 * move along it.
 */
export function PageTabs({ tabs, className }: { tabs: readonly PageTab[]; className?: string }) {
  return (
    <ScrollableStrip
      as="nav"
      aria-label="Sections"
      className={cn(
        'flex items-end gap-4 overflow-x-auto border-b border-line px-4 [scrollbar-width:none] lg:px-8 [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          state={tab.state}
          className={({ isActive }) =>
            cn(
              'relative -mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-md border-b-2 px-1 pb-[11px] text-base font-semibold transition-colors',
              'outline-none focus-visible:ring-4 focus-visible:ring-primary-100',
              isActive
                ? 'border-primary-500 text-accent-text'
                : 'border-transparent text-ink-subtle hover:text-ink',
            )
          }
        >
          {({ isActive }) => (
            <>
              {tab.label}
              {tab.count === undefined ? null : (
                <span
                  className={cn(
                    'inline-flex h-[22px] items-center rounded-full border px-2 text-xs font-medium tabular-nums',
                    isActive
                      ? 'border-accent-soft-border bg-accent-soft text-accent-text'
                      : 'border-neutral-soft-border bg-neutral-soft text-neutral-text',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </ScrollableStrip>
  )
}
